/* eslint-disable no-param-reassign */
import { ComponentType } from 'react';
import clsx from 'clsx';
import { FundOwnershipDetailModel } from 'api';
import { securityTypes } from 'common/constants';
import * as capCells from 'common/constants/cap-table';
import { LEDGER, NUMBER, PERCENTAGE } from 'common/constants/gridType';
import {
  capTableCurrencyFormat,
  capTableSharesFormat,
  capTableTwoDecimalPercentFormat,
  capTableXSuffixFormat,
  fullyDilutedOwnershipFormat,
  largeCurrencyFormat,
  oneDecimalPercentFormatValidateFloatTrue,
  xLargeSuffixFormat,
  xStandardSuffixFormat,
} from 'common/formats/formats';
import { Fund } from 'common/types/fund';
import {
  DataEditorProps,
  DataEditorType,
  ValueViewerProps,
  ValueViewerType,
} from 'common/types/scalarSpreadsheet';
import { SelectValueViewer } from 'components';
import { SelectValueViewerProps } from 'components/SelectValueViewer/SelectValueViewer.props';
import { DividendRateVariableChip, SecurityType, UnderlyingSecurity } from 'pages/CapTable/cap-table/components';
import { SecurityTypeProps } from 'pages/CapTable/cap-table/components/security-type/SecurityType.props';
import { UnderlyingSecurityProps } from 'pages/CapTable/cap-table/components/underlying-security/UnderlyingSecurity.props';
import { CONVERTIBLE_NOTES } from '../../../../common/constants/securityTypes';
import LiquidationPreferenceVariableChip from '../components/liquidation-preference-variable/LiquidationPreferenceVariableChip';

// Add component prop types for row config usage (to avoid typing with any)
const SecurityTypeEditor = SecurityType as unknown as ComponentType<SecurityTypeProps>;
const UnderlyingSecurityEditor = UnderlyingSecurity as unknown as ComponentType<UnderlyingSecurityProps>;

export type RowConfigProps = {
  customTitles?: Record<string, string>;
  measurementDate: string;
  isDisabled: boolean;
  fundOwnershipDetails?: FundOwnershipDetailModel[];
  fundList?: Fund[] | null;
  firmName: string;
};

export const getAccruedDividendsTxt = (measurementDate: string) => `Accrued Dividends (as of ${measurementDate})`;

// note that this function gets called in a plethora of places!
const rowConfig = (props: RowConfigProps) => {
  const { customTitles = {}, measurementDate, isDisabled, fundOwnershipDetails, fundList, firmName } = props;
  const tmpConfig = [
    // 01.  Security Name
    {
      value: 'Security Name',
      alias: capCells.SECURITY_NAME_ALIAS,
      hidden: false,
      readOnly: isDisabled,
      hasSubRows: true,
      showSubRows: true,
      expr: '',
      defaultValue: 'ENTER NAME',
      tooltipPlacement: 'bottom',
      className: clsx('table-header division-bottom-only', { 'text-blue': !isDisabled }),
    },
    // 02.  Legal Name
    {
      value: 'Legal Name',
      alias: capCells.LEGAL_NAME_ALIAS,
      parent: capCells.SECURITY_NAME_ALIAS,
      hidden: false,
      readOnly: isDisabled,
      expr: '',
      defaultValue: '',
    },
    // 03.  Investment Date
    {
      value: 'Investment Date',
      alias: capCells.INVESTMENT_DATE_ALIAS,
      dataSource: 'investment_dates',
      expr: '',
      dbType: 'dbDate',
      gridType: 'gridDate',
      allowEmptyValues: true,
      isRequired: true,
    },
    // 04.  Security type
    {
      value: 'Security Type',
      alias: capCells.SECURITY_TYPE_ALIAS,
      dataEditor: ((editorProps: DataEditorProps) => (
        <SecurityTypeEditor
          {...{
            ...editorProps,
            options: securityTypes.LIST,
          } as unknown as SecurityTypeProps}
        />
      )) as DataEditorType,
      valueViewer: ((viewerProps: ValueViewerProps) => (
        <SelectValueViewer {...(viewerProps as unknown as SelectValueViewerProps)} />
      )) as ValueViewerType,
      hidden: false,
      readOnly: isDisabled,
      expr: '',
      gridType: 'number',
      dbType: 'number',
      displayUndefined: true,
      linkedCellSymbols: [
        `@${capCells.INVESTMENT_DATE_ROW_NUMBER}`,
        `@${capCells.ISSUE_PRICE_ROW_NUMBER}`,
        `@${capCells.SHARES_OUTSTANDING_ROW_NUMBER}`,
        `@${capCells.SHARES_FULLY_DILUTED_ROW_NUMBER}`,
        `@${capCells.STRIKE_PRICE_ROW_NUMBER}`,
        `@@${capCells.UNDERLYING_SECURITY_ROW_NUMBER}`,
        `@${capCells.CONVERSION_RATE_ROW_NUMBER}`,
        `@${capCells.PIK_SHARES_ROW_NUMBER}`,
        `@${capCells.LIQUIDATION_PREFERENCE_ROW_NUMBER}`,
        `@${capCells.SENIORITY_ROW_NUMBER}`,
        `@${capCells.PARTICIPATION_ROW_NUMBER}`,
        `@${capCells.CUMULATIVE_DIVIDENDS_ROW_NUMBER}`,
      ],
    },
    // 05.  Original Issue Price
    {
      value: 'Original Issue Price',
      defaultValue: '',
      alias: capCells.ISSUE_PRICE_ALIAS,
      readOnly: true,
      format: capTableCurrencyFormat,
      expr: '',
      gridType: 'number',
      dbType: 'string',
    },
    // 06.  Shares Outstanding
    {
      value: 'Shares Outstanding',
      alias: capCells.SHARES_OUTSTANDING_ALIAS,
      hidden: false,
      exprBuilderEnabled: true,
      hasTotal: true,
      format: capTableSharesFormat,
      dataSource: 'options_ledger',
      readOnly: false,
      expr: '',
      gridType: 'number',
      dbType: 'string',
      defaultValue: '',
    },
    // 07.  Conversion Rate
    {
      value: 'Conversion Rate',
      defaultValue: '',
      alias: capCells.CONVERSION_RATE_ALIAS,
      readOnly: true,
      hidden: true,
      format: capTableXSuffixFormat,
      expr: '',
      gridType: 'number',
      dbType: 'string',
      className: 'subtitle-indented',
    },
    // 08.  PIK Shares as of today
    {
      value: 'PIK Shares as of Today',
      alias: capCells.PIK_SHARES_ALIAS,
      readOnly: true,
      format: capTableSharesFormat,
      expr: `=IF(@${capCells.DIVIDEND_PAYMENT_TYPE_ROW_NUMBER}==Pik,@${capCells.ACCRUED_DIVIDENDS_ROW_NUMBER}/@${capCells.ISSUE_PRICE_ROW_NUMBER},0)`,
      gridType: 'number',
      dbType: 'string',
      evalOrder: 1,
    },
    // 09.  Shares Fully Diluted (as converted)
    {
      value: 'Shares Fully Diluted (as converted)',
      alias: capCells.SHARES_FULLY_DILUTED_ALIAS,
      hidden: false,
      readOnly: true,
      hasTotal: true,
      format: capTableSharesFormat,
      expr: `=IF(OR(@${capCells.SECURITY_TYPE_ROW_NUMBER}==Preferred_Stock,@${capCells.SECURITY_TYPE_ROW_NUMBER}==Warrant),(@${capCells.SHARES_OUTSTANDING_ROW_NUMBER}+@${capCells.PIK_SHARES_ROW_NUMBER})*@${capCells.CONVERSION_RATE_ROW_NUMBER},@${capCells.SHARES_OUTSTANDING_ROW_NUMBER})`,
      gridType: 'number',
      dbType: 'string',
      evalOrder: 2,
    },
    // 10.  Current Ownership
    {
      value: 'Current Ownership',
      defaultValue: '0',
      alias: capCells.CURRENT_OWNERSHIP_ALIAS,
      readOnly: true,
      hidden: false,
      className: 'subtitle',
      hasTotal: true,
      format: oneDecimalPercentFormatValidateFloatTrue,
      dbDecimalPlaces: 15,
      gridType: 'percentage',
      dbType: 'string',
    },
    // 11.  Fully Diluted Ownership
    {
      value: 'Fully Diluted Ownership',
      defaultValue: '0',
      alias: capCells.FULLY_DILUTED_OWNERSHIP_ALIAS,
      readOnly: true,
      hidden: false,
      hasTotal: true,
      format: fullyDilutedOwnershipFormat,
      dbDecimalPlaces: 15,
      gridType: 'percentage',
      dbType: 'string',
    },
    // 12.  Strike Price
    {
      value: 'Strike Price',
      defaultValue: '',
      alias: capCells.STRIKE_PRICE_ALIAS,
      hasSubRows: true,
      showSubRows: true,
      dataSource: 'options_ledger',
      format: capTableCurrencyFormat,
      expr: '',
      gridType: 'number',
      dbType: 'string',
      disableWhenLedgerOpen: true,
      readOnly: true,
    },
    // 13.  Underlying Security
    {
      value: 'Underlying Security',
      alias: capCells.UNDERLYING_SECURITY_ALIAS,
      dataEditor: ((editorProps: DataEditorProps) => (
        <UnderlyingSecurityEditor {...(editorProps as unknown as UnderlyingSecurityProps)} />
      )) as DataEditorType,
      valueViewer: ((viewerProps: ValueViewerProps) => (
        <SelectValueViewer {...(viewerProps as unknown as SelectValueViewerProps)} />
      )) as ValueViewerType,
      parent: capCells.STRIKE_PRICE_ALIAS,
      expr: '',
      linkedCellSymbols: [
        `@${capCells.STRIKE_PRICE_ROW_NUMBER}`,
        `@${capCells.SHARES_OUTSTANDING_ROW_NUMBER}`,
        `@${capCells.CONVERSION_RATE_ROW_NUMBER}`,
      ],
    },
    // 14.  Preferred Terms
    {
      value: 'Preferred Terms',
      defaultValue: '0',
      alias: capCells.PREFERRED_TERMS_ALIAS,
      hasSubRows: true,
      showSubRows: true,
      expr: '',
      readOnly: true,
    },
    // 15.  Liquidation Preference
    {
      value: 'Liquidation Preference',
      defaultValue: '',
      alias: capCells.LIQUIDATION_PREFERENCE_ALIAS,
      parent: capCells.PREFERRED_TERMS_ALIAS,
      format: capTableXSuffixFormat,
      expr: '',
      gridType: 'number',
      dbType: 'string',
      sideComponent: <LiquidationPreferenceVariableChip />,
    },
    // 16.  Seniority
    {
      parent: capCells.PREFERRED_TERMS_ALIAS,
      value: 'Liquidation Priority',
      defaultTooltip:
        'The most senior liquidation priority should be given a \'1\', next senior assigned a \'2\', etc. Securities that are pari passu should be assigned the same liquidation priority number.',
      alias: capCells.SENIORITY_ALIAS,
      expr: '',
      gridType: 'number',
      dbType: 'number',
      displayUndefined: true,
    },
    // 17.  Participating
    {
      parent: capCells.PREFERRED_TERMS_ALIAS,
      value: 'Participating',
      alias: capCells.PARTICIPATION_ALIAS,
      expr: '',
      gridType: 'number',
      dbType: 'boolean',
      displayUndefined: true,
      linkedCellSymbols: [`@${capCells.IS_PARTICIPATION_CAP_ROW_NUMBER}`],
    },
    // 18.  Participation Cap
    {
      parent: capCells.PREFERRED_TERMS_ALIAS,
      value: 'Participation Cap',
      defaultValue: '0',
      alias: capCells.IS_PARTICIPATION_CAP_ALIAS,
      expr: '',
      gridType: 'number',
      dbType: 'boolean',
      displayUndefined: true,
      linkedCellSymbols: [`@${capCells.PARTICIPATION_CAP_ROW_NUMBER}`],
    },
    // 19.  Participation Cap (X)
    {
      value: 'Participation Cap (X)',
      defaultValue: '0',
      alias: capCells.PARTICIPATION_CAP_ALIAS,
      parent: capCells.PREFERRED_TERMS_ALIAS,
      format: xStandardSuffixFormat,
      expr: '',
      gridType: 'number',
      dbType: 'string',
    },
    // 20.  Cumulative Dividends
    {
      value: 'Cumulative Dividends',
      alias: capCells.CUMULATIVE_DIVIDENDS_ALIAS,
      parent: capCells.PREFERRED_TERMS_ALIAS,
      readOnly: true,
      expr: '',
      gridType: 'number',
      dbType: 'boolean',
      displayUndefined: true,
      linkedCellSymbols: [
        `@${capCells.HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER}`,
        `@${capCells.DIVIDEND_PAYMENT_TYPE_ROW_NUMBER}`,
        `@${capCells.DIVIDEND_RATE_ROW_NUMBER}`,
        `@${capCells.COMPOUNDING_ROW_NUMBER}`,
        `@${capCells.COMPOUNDING_PERIOD_ROW_NUMBER}`,
      ],
    },
    // 21.  Multiple Investment Dates?
    {
      value: 'Multiple Investment Dates?',
      alias: capCells.HAS_MULTIPLE_INVESTMENT_DATES_ALIAS,
      parent: capCells.PREFERRED_TERMS_ALIAS,
      readOnly: true,
      hidden: true,
      disableEvents: false,
      expr: '',
      gridType: 'number',
      dbType: 'boolean',
      disableWhenLedgerOpen: true,
      displayUndefined: true,
      linkedCellSymbols: [
        `@${capCells.SHARES_OUTSTANDING_ROW_NUMBER}`,
        `@${capCells.ACCRUED_DIVIDENDS_ROW_NUMBER}`,
        `@${capCells.DIVIDEND_RATE_ROW_NUMBER}`,
        `@${capCells.LIQUIDATION_PREFERENCE_ROW_NUMBER}`,
      ],
    },
    // 22.  Cash or PIK
    {
      value: 'Cash or PIK',
      alias: capCells.DIVIDEND_PAYMENT_TYPE_ALIAS,
      parent: capCells.PREFERRED_TERMS_ALIAS,
      readOnly: true,
      expr: '',
      gridType: 'number',
      dbType: 'number',
      displayUndefined: true,
    },
    // 23.  Dividend Rate
    {
      value: 'Dividend Rate',
      defaultValue: '0',
      alias: capCells.DIVIDEND_RATE_ALIAS,
      parent: capCells.PREFERRED_TERMS_ALIAS,
      readOnly: true,
      format: capTableTwoDecimalPercentFormat,
      dbDecimalPlaces: 3,
      expr: '',
      gridType: 'percentage',
      dbType: 'string',
      sideComponent: <DividendRateVariableChip />,
    },
    // 24.  Compounding
    {
      value: 'Compounding',
      alias: capCells.COMPOUNDING_ALIAS,
      parent: capCells.PREFERRED_TERMS_ALIAS,
      expr: '',
      gridType: 'number',
      dbType: 'boolean',
      displayUndefined: true,
      linkedCellSymbols: [`@${capCells.COMPOUNDING_PERIOD_ROW_NUMBER}`, `@${capCells.ACCRUED_DIVIDENDS_ROW_NUMBER}`],
    },
    // 25.  Compounding Periods/Year
    {
      value: 'Compounding Periods/Year',
      alias: capCells.COMPOUNDING_PERIOD_ALIAS,
      parent: capCells.PREFERRED_TERMS_ALIAS,
      format: xLargeSuffixFormat,
      expr: '',
      gridType: 'number',
      dbType: 'string',
      minValue: 1,
      allowDecimals: false,
      allowNegativeValue: false,
    },
    // 26.  Accrued Dividends (as of 12/31/2018)
    {
      value: getAccruedDividendsTxt(measurementDate),
      defaultValue: '0',
      readonly: true,
      alias: capCells.ACCRUED_DIVIDENDS_ALIAS,
      format: largeCurrencyFormat,
      type: 'string',
      // In case we update/change this formula, we need to update the formula
      // on the backend as well (app.cap_table.models.Security.calculate_accrued_dividends)
      expr: `=IF(@${capCells.SECURITY_TYPE_ROW_NUMBER}!=${CONVERTIBLE_NOTES}, IF(@${capCells.HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER}==N, IF(@${capCells.COMPOUNDING_ROW_NUMBER}==Y,(@${capCells.ISSUE_PRICE_ROW_NUMBER} * (1+@${capCells.DIVIDEND_RATE_ROW_NUMBER}/@${capCells.COMPOUNDING_PERIOD_ROW_NUMBER}) ^ (YEARFRAC(@${capCells.INVESTMENT_DATE_ROW_NUMBER}, '${measurementDate}', yearfracBasis) * @${capCells.COMPOUNDING_PERIOD_ROW_NUMBER}) - @${capCells.ISSUE_PRICE_ROW_NUMBER}) * @${capCells.SHARES_OUTSTANDING_ROW_NUMBER},@${capCells.DIVIDEND_RATE_ROW_NUMBER}*@${capCells.ISSUE_PRICE_ROW_NUMBER}*@${capCells.SHARES_OUTSTANDING_ROW_NUMBER}*YEARFRAC(@${capCells.INVESTMENT_DATE_ROW_NUMBER}, '${measurementDate}', yearfracBasis)), SUM_FROM_ACCRUED_LEDGER(@${capCells.MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER}, @${capCells.COMPOUNDING_ROW_NUMBER}, @${capCells.ISSUE_PRICE_ROW_NUMBER}, @${capCells.DIVIDEND_RATE_ROW_NUMBER}, @${capCells.COMPOUNDING_PERIOD_ROW_NUMBER}, '${measurementDate}')), ACCRUED_FROM_CN_LEDGER(@${capCells.CONVERTIBLE_NOTES_ROW_NUMBER}, @${capCells.COMPOUNDING_ROW_NUMBER}, @${capCells.ISSUE_PRICE_ROW_NUMBER}, @${capCells.DIVIDEND_RATE_ROW_NUMBER}, @${capCells.COMPOUNDING_PERIOD_ROW_NUMBER}, '${measurementDate}'))`,
      altExpr: `=@${capCells.MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER}`,
      gridType: 'number',
      dbType: 'string',
      evalOrder: 0,
      disableWhenLedgerOpen: true,
    },
    // 27.  Initial Liquidation Preference
    {
      value: 'Initial Liquidation Preference',
      defaultValue: '0',
      alias: capCells.INITIAL_LIQUIDATION_PREFERENCE_ALIAS,
      hidden: false,
      readOnly: true,
      hasTotal: true,
      className: 'subtitle',
      format: largeCurrencyFormat,
      expr: `=IF(@${capCells.SECURITY_TYPE_ROW_NUMBER}==${CONVERTIBLE_NOTES}, CONVERTIBLE_NOTE_INIT_LIQUIDATION_PREFERENCE(@${capCells.CONVERTIBLE_NOTES_ROW_NUMBER},@${capCells.LIQUIDATION_PREFERENCE_ROW_NUMBER}), @${capCells.SHARES_OUTSTANDING_ROW_NUMBER}*@${capCells.ISSUE_PRICE_ROW_NUMBER}*@${capCells.LIQUIDATION_PREFERENCE_ROW_NUMBER})`, // Shares outsanding * original issue price * liquidation preference
      gridType: 'number',
      dbType: 'string',
    },
    // 28.  Total Preference (with Dividends)
    {
      value: 'Total Preference (with Dividends)',
      defaultValue: '0',
      alias: capCells.TOTAL_PREFERENCE_ALIAS,
      readOnly: true,
      hasTotal: true,
      className: 'subtitle',
      format: largeCurrencyFormat,
      expr: `=@${capCells.ACCRUED_DIVIDENDS_ROW_NUMBER}+@${capCells.INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER}`,
      gridType: 'number',
      dbType: 'string',
    },
    // 29.  multiple_investments
    {
      value: 'Multiple Investments',
      alias: capCells.MULTIPLE_INVESTMENT_LEDGER_ALIAS,
      gridType: LEDGER,
      isVisible: false,
      linkedCellSymbols: [`@${capCells.HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER}`],
    },
    // 30.  options_ledger
    {
      value: 'Options Ledger',
      alias: capCells.OPTIONS_LEDGER_ALIAS,
      gridType: LEDGER,
      isVisible: false,
    },
    // 31. Security Reference
    {
      value: 'Security Reference',
      alias: capCells.SECURITY_REFERENCE_ALIAS,
      isVisible: false,
    },
    // 32. Convertible Notes
    {
      value: capCells.CONVERTIBLE_NOTES_TITLE,
      alias: capCells.CONVERTIBLE_NOTES_ALIAS,
      isVisible: false,
      hidden: true,
      gridType: LEDGER,
      linkedCellSymbols: [
        `@${capCells.LIQUIDATION_PREFERENCE_ROW_NUMBER}`,
        `@${capCells.SENIORITY_ROW_NUMBER}`,
        `@${capCells.PARTICIPATION_ROW_NUMBER}`,
        `@${capCells.CUMULATIVE_DIVIDENDS_ROW_NUMBER}`,
      ],
    },
    // 33. Total Note Principal
    {
      value: capCells.TOTAL_NOTE_PRINCIPAL_TITLE,
      alias: capCells.TOTAL_NOTE_PRINCIPAL_ALIAS,
      isVisible: false,
    },
    // 34. Total Note Value At Measurement Date
    {
      value: capCells.TOTAL_NOTE_VALUE_AT_MD_TITLE,
      alias: capCells.TOTAL_NOTE_VALUE_AT_MD_ALIAS,
      isVisible: false,
    },
    // 35. Liquidation Preference Ledger
    {
      value: capCells.LIQUIDATION_PREFERENCE_LEDGER_TITLE,
      alias: capCells.LIQUIDATION_PREFERENCE_LEDGER_ALIAS,
      isVisible: false,
    },
    // 36. Dividend Rate Ledger
    {
      value: capCells.DIVIDEND_RATE_LEDGER_TITLE,
      alias: capCells.DIVIDEND_RATE_LEDGER_ALIAS,
      isVisible: false,
    },
  ];

  if (fundOwnershipDetails && fundOwnershipDetails.length > 0) {
    const fundIdMap: { [key: number]: Fund } = (fundList || [])?.reduce(
      (acc, fund) => {
        acc[fund.id as number] = fund;
        return acc;
      },
      {} as { [key: number]: Fund }
    );
    const fundOwnershipByEntity = fundOwnershipDetails?.reduce(
      (acc, fod) => {
        if (fod.fund) {
          if (!acc[fod.fund]) {
            acc[fod.fund] = [];
          }
          acc[fod.fund].push(fod);
        }
        return acc;
      },
      {} as { [key: number]: FundOwnershipDetailModel[] }
    );

    // Parent rows first so the array type allows optional child-only `data` ({ fodList, fundId }).
    const fundSharesRows = [
      {
        value: `${firmName} Shares`,
        alias: 'firm_shares',
        gridType: NUMBER,
        hasSubRows: true,
        showSubRows: true,
        format: capTableSharesFormat,
        hasTotal: true,
      },
      ...Object.values(fundOwnershipByEntity).map(fodList => {
        const fundId = fodList[0].fund;
        if (fundId) {
          return ({
            value: `${fundIdMap[fundId]?.name}`,
            alias: `fund_shares_${fundId}`,
            gridType: NUMBER,
            parent: 'firm_shares',
            data: {fodList, fundId},
            hasTotal: true,
            format: capTableSharesFormat,
          });
        }
        return undefined
      }).filter(row => row !== undefined),
    ];
    const fundPercentRows = [
      {
        value: `${firmName} Ownership %`,
        alias: 'firm_ownership_percentage',
        hasSubRows: true,
        showSubRows: true,
        gridType: PERCENTAGE,
        format: oneDecimalPercentFormatValidateFloatTrue,
        hasTotal: true,
      },
      ...Object.values(fundOwnershipByEntity).map(fodList => {
        const fundId = fodList[0].fund;
        if (fundId) {
          return ({
            value: `${fundIdMap[fundId]?.name}`,
            alias: `fund_ownership_percentage_${fundId}`,
            parent: 'firm_ownership_percentage',
            gridType: PERCENTAGE,
            data: {fodList, fundId},
            format: oneDecimalPercentFormatValidateFloatTrue,
            hasTotal: true,
          });
        }
        return undefined
      }).filter(row => row !== undefined),
    ];

    const fundLiqPrefRows = [
      {
        value: `${firmName} Liquidation Preference`,
        alias: 'firm_liquidation_preference',
        hasSubRows: true,
        showSubRows: true,
        gridType: NUMBER,
        format: largeCurrencyFormat,
        hasTotal: true,
      },
      ...Object.values(fundOwnershipByEntity).map(fodList => {
        const fundId = fodList[0].fund;
        if (fundId) {
          return ({
            value: `${fundIdMap[fundId]?.name}`,
            alias: `fund_liquidation_preference_${fundId}`,
            parent: 'firm_liquidation_preference',
            gridType: NUMBER,
            data: {fodList, fundId},
            format: largeCurrencyFormat,
            hasTotal: true,
          });
        }
        return undefined
      }).filter(row => row !== undefined),
    ];

    tmpConfig.push(
      {alias: capCells.DIVIDER_BLANK_ROW_ALIAS, value: capCells.DIVIDER_BLANK_ROW_TITLE, readOnly: true} as (typeof tmpConfig)[number],
      ...(fundPercentRows as unknown as typeof tmpConfig),
      ...(fundSharesRows as unknown as typeof tmpConfig),
      ...(fundLiqPrefRows as unknown as typeof tmpConfig),
    )
  }

  return tmpConfig.map(config => {
    config.value = customTitles[config.alias] ?? config.value;
    return config;
  });
};

export default rowConfig;
